import { PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import { ActionIcon, Button, Loader, Modal, Table, Text } from '@mantine/core';
import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';

import { deleteAdminBook } from '@/entities/books/api/admin-books-api';
import { Book, BookCategories } from '@/entities/books/model/types';
import { getCategories } from '@/shared/api/categories';
import {
  useCurrentProject,
  useProjectPath,
} from '@/shared/projects/project-path';

import { generateBookCategories } from '../services/generateBookCategories';
import styles from './book-table.module.scss';

interface BookListProps {
  books: Book[];
  setBooksList: (books: Book[]) => void;
  loading?: boolean;
}

export const BookTable: React.FC<BookListProps> = ({
  books,
  setBooksList,
  loading = false,
}) => {
  const [categories, setCategories] = useState<BookCategories[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [bookToDelete, setBookToDelete] = useState<number | null>(null);
  const navigate = useNavigate();
  const project = useCurrentProject();
  const withProject = useProjectPath();

  const fetchCategories = async () => {
    try {
      const { categories } = await getCategories(project);
      setCategories(categories as BookCategories[]);
    } catch (error) {
      console.error('Ошибка при получении категорий книг:', error);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, [project]);

  const bookCategories = generateBookCategories(books, categories);

  const handleRowClick = (bookId: number) => {
    navigate(withProject(`/books/${bookId}/description`));
  };

  const handleActionClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row click when clicking action buttons
  };

  const handleDelete = async () => {
    if (bookToDelete !== null) {
      try {
        await deleteAdminBook(project, bookToDelete);

        const updatedBooks = books.filter((book) => book.id !== bookToDelete);
        setBooksList(updatedBooks);
        setIsModalOpen(false);
        setBookToDelete(null);
      } catch (error) {
        console.error('Ошибка при удалении книги:', error);
      }
    }
  };

  const openDeleteModal = (id: number) => {
    setBookToDelete(id);
    setIsModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsModalOpen(false);
    setBookToDelete(null);
  };

  if (loading) {
    return (
      <div
        style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}
      >
        <Loader size="lg" />
      </div>
    );
  }

  if (books.length === 0) {
    return <Text>Нет доступных книг для отображения</Text>;
  }

  return (
    <>
      <Table
        highlightOnHover
        withTableBorder
        verticalSpacing="lg"
        horizontalSpacing="lg"
        className={styles.table}
        borderColor="#F8F8F8"
      >
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Статус</Table.Th>
            <Table.Th>Название</Table.Th>
            <Table.Th>Автор</Table.Th>
            <Table.Th>Категория</Table.Th>
            <Table.Th>Обложка</Table.Th>
            <Table.Th></Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody className={styles.tableBody}>
          {books.map((book) => (
            <Table.Tr
              key={book.id}
              onClick={() => handleRowClick(book.id)}
              style={{ cursor: 'pointer' }}
            >
              <Table.Td>
                <div
                  className={clsx(styles.badge, {
                    [styles.published]: book.isPublished,
                  })}
                >
                  <Text>{book.isPublished ? 'Опубликовано' : 'Черновик'}</Text>
                </div>
              </Table.Td>
              <Table.Td className={styles.title}>
                <Text>{book.title}</Text>
              </Table.Td>
              <Table.Td>
                <Text>{book.author}</Text>
              </Table.Td>
              <Table.Td>
                <Text>{bookCategories[book.id]?.join(', ') || ''}</Text>
              </Table.Td>
              <Table.Td>
                <img
                  src={book.imgUrl}
                  alt={book.title}
                  className={styles.bookImage}
                />
              </Table.Td>
              <Table.Td
                className={styles.actionButtons}
                onClick={handleActionClick}
              >
                <NavLink
                  to={withProject(`/books/${String(book.id)}/description`)}
                >
                  <ActionIcon className={styles.button}>
                    <PencilSquareIcon width={20} height={20} />
                  </ActionIcon>
                </NavLink>
                <ActionIcon
                  className={styles.button}
                  onClick={() => openDeleteModal(book.id)}
                >
                  <TrashIcon width={20} height={20} />
                </ActionIcon>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      <Modal
        opened={isModalOpen}
        onClose={closeDeleteModal}
        title="Подтвердите удаление"
      >
        <Text>Вы уверены, что хотите удалить эту книгу?</Text>
        <div className={styles.deleteBtn}>
          <Button color="red" onClick={handleDelete} className={styles.redBtn}>
            Удалить
          </Button>
          <Button onClick={closeDeleteModal}>Отмена</Button>
        </div>
      </Modal>
    </>
  );
};
